import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface User {
    id: number;
    email: string;
    username?: string;
    translater?: boolean;
    admin?: boolean;
    translater_admin?: boolean;
}

const AdminUser = () => {
    const queryClient = useQueryClient();
    const [users, setUsers] = useState<User[]>([]);

    const { data: usersData, isLoading } = useQuery({
        queryKey: ["adminUsers"],
        queryFn: async () => {
            const response = await fetch("/admin/users");
            if (!response.ok) throw new Error("Failed to fetch users");
            return response.json();
        },
    }); useEffect(() => {
        if (usersData) {
            setUsers(usersData);
        }
    }, [usersData]);

    const updateUserMutation = useMutation({
        mutationFn: async (user: User) => {
            const response = await fetch(`/admin/users/${user.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    user: {
                        admin: user.admin,
                        translater: user.translater,
                        translater_admin: user.translater_admin,
                    },
                }),
            });
            if (!response.ok) throw new Error("Failed to update user");
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Felhasználó frissítve" });
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
        },
        onError: () => {
            toast({ title: "Hiba történt", variant: "destructive" });
        },
    });

    const handleCheckboxChange = (userId: number, field: keyof User, value: boolean) => {
        setUsers((prevUsers) =>
            prevUsers.map((user) =>
                user.id === userId ? { ...user, [field]: value } : user
            )
        );
    };

    const handleSave = (user: User) => {
        updateUserMutation.mutate(user);
    };

    if (isLoading) return <p>Betöltés...</p>;

    return (
        <div className="container mx-auto p-4">
            <Card>
                <CardHeader>
                    <CardTitle>Felhasználók kezelése</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Email</TableHead>
                                <TableHead>Username</TableHead>
                                <TableHead>Admin</TableHead>
                                <TableHead>Translater Admin</TableHead>
                                <TableHead>Translater</TableHead>
                                <TableHead>Műveletek</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.username || '-'}</TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={user.admin || false}
                                            onCheckedChange={(checked) =>
                                                handleCheckboxChange(user.id, "admin", checked as boolean)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={user.translater_admin || false}
                                            onCheckedChange={(checked) =>
                                                handleCheckboxChange(user.id, "translater_admin", checked as boolean)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={user.translater || false}
                                            onCheckedChange={(checked) =>
                                                handleCheckboxChange(user.id, "translater", checked as boolean)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            onClick={() => handleSave(user)}
                                            disabled={updateUserMutation.isPending}
                                        >
                                            {updateUserMutation.isPending ? "Mentés..." : "Mentés"}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminUser;